import { useCallback } from 'react';

import { ensureDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../../../utils/back-errors.util';
import type { BackError } from '../../../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import type { SubmitHandler } from '../types/submit-handler.type';

import { useRemoveThirdPartyAddressMutation } from './useRemoveThirdPartyAddressMutation.hook';
import { useFindAddressIndexById } from './useFindIndexById.hook';

interface Params {
  readonly addressId?: Id | null;
  readonly handleRemoveSuccess: () => void;
  readonly thirdPartyId: Id;
  readonly useDirectMutation: boolean;
}

interface FormikArrayActions {
  readonly removeInForm: (index: number) => void;
}

export const useRemoveThirdPartyAddressSubmit = ({
  addressId,
  handleRemoveSuccess,
  thirdPartyId,
  useDirectMutation,
}: Readonly<Params>) => {
  const { mutate: removeMutation } = useRemoveThirdPartyAddressMutation();

  const findIndexById = useFindAddressIndexById();

  const handleSubmitFactory = useCallback(
    ({ removeInForm }: Readonly<FormikArrayActions>): SubmitHandler =>
      ({ memos, formikHelpers: { setSubmitting }, setFormError }) => {
        const { addressId: definedAddressId } = ensureDefined({ addressId });

        setFormError(null);

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));
          setSubmitting(false);
        };

        const onSuccess = () => {
          const index = findIndexById(definedAddressId);

          if (index === -1) {
            setFormError(["L'adresse n'existe pas"]);
            setSubmitting(false);
            return;
          }

          removeInForm(index);

          handleRemoveSuccess();
          setSubmitting(false);
        };

        if (useDirectMutation) {
          removeMutation(
            {
              addressId: definedAddressId,
              thirdPartyId,
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [
      addressId,
      findIndexById,
      handleRemoveSuccess,
      removeMutation,
      thirdPartyId,
      useDirectMutation,
    ],
  );

  return { handleSubmitFactory };
};
